import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Check, ShieldCheck } from "lucide-react";
import { LOGO_URL } from "../lib/api";

const PERKS = [
  "Save unlimited business ideas",
  "Create your Startup Notebook",
  "Generate AI Builder Blueprints",
];

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export const LoginModal = ({ open, onClose, onGoogle, reason }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const COPY = {
    idea_limit: {
      title: "Unlock this business idea",
      subtitle: "Sign in to continue — then unlock every idea, blueprint and report with Pro.",
    },
    premium_feature: {
      title: "Premium Founder feature",
      subtitle: "Create your free account to continue.",
    },
    scanner: {
      title: "AI Opportunity Scanner",
      subtitle: "Turn anything you see into your next startup.",
      perks: [
        "Scan real-world problems",
        "Discover hidden opportunities",
        "Generate startup ideas in seconds",
      ],
    },
    default: {
      title: "Create your free Founder Account",
      subtitle: "Save ideas, keep notes, and generate Builder Prompts.",
    },
  };
  const c = COPY[reason] || COPY.default;
  const perks = c.perks || PERKS;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-[#0F0F0F]/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="login-overlay"
        >
          <motion.div
            className="bg-white rounded-3xl w-full max-w-md shadow-2xl relative overflow-hidden"
            initial={{ scale: 0.95, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 12 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-testid="login-modal"
          >
            <button
              onClick={onClose}
              className="absolute top-5 right-5 text-white/70 hover:text-white transition-colors z-10"
              data-testid="login-close-x"
            >
              <X size={22} />
            </button>

            <div className="bg-[#0F0F0F] px-8 pt-10 pb-12 text-center relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-60"
                style={{ background: "radial-gradient(circle at 50% 20%, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0) 55%)" }}
              />
              <div className="relative">
                <img src={LOGO_URL} alt="LaunchBS" className="h-14 w-14 object-contain mx-auto mb-4 drop-shadow-[0_10px_30px_rgba(212,175,55,0.4)]" />
                <h2 className="font-display text-2xl font-bold text-white" data-testid="login-title">
                  {c.title}
                </h2>
                <p className="text-sm text-[#A1A1AA] mt-2 max-w-xs mx-auto">
                  {c.subtitle}
                </p>
              </div>
            </div>

            <div className="px-8 py-8">
              <ul className="space-y-2.5 mb-7">
                {perks.map((p) => (
                  <li key={p} className="flex items-center gap-3 text-sm text-[#0F0F0F]">
                    <span className="w-5 h-5 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center shrink-0">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    {p}
                  </li>
                ))}
              </ul>

              <button
                onClick={onGoogle}
                className="w-full inline-flex items-center justify-center gap-3 bg-white border border-gray-200 rounded-full py-3.5 text-sm font-semibold text-[#0F0F0F] hover:bg-[#F7F7F7] hover:border-gray-300 transition-colors shadow-sm"
                data-testid="continue-google-btn"
              >
                <GoogleIcon /> Continue with Google
              </button>
              <div className="mt-4 flex items-start gap-2.5 rounded-2xl bg-[#F7F7F7] border border-gray-100 px-4 py-3" data-testid="login-privacy-note">
                <span className="w-6 h-6 rounded-full bg-[#D4AF37]/15 text-[#B8901F] flex items-center justify-center shrink-0 mt-0.5">
                  <ShieldCheck size={14} />
                </span>
                <p className="text-xs text-[#687280] leading-relaxed text-left">
                  Google will ask you to allow LaunchBS to see your <span className="font-semibold text-[#0F0F0F]">name, email &amp; profile photo</span> — that's all we receive.
                  We <span className="font-semibold text-[#0F0F0F]">can't read your Gmail</span>, emails, contacts, or files, and there are no passwords to manage.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
